import { Link } from 'react-router-dom';
import { Lock, Trophy } from 'lucide-react';
import { TierFeatureKey, getRequiredTier } from '@court-sync/shared';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { Button } from '@/components/ui';
import { useMatchPlayAccess } from '@/hooks/useMatchPlayAccess';

const SETTINGS_SUBSCRIPTION_HREF = '/settings?tab=subscription';

export interface PlayFeatureGateProps {
  children: React.ReactNode;
  title?: string;
  description?: string;
}

/**
 * Gates match play surfaces when the organization lacks MATCH_PLAY entitlement
 * (Professional+ academy tier or club module).
 */
export function PlayFeatureGate({
  children,
  title = 'Match play not available on your plan',
  description,
}: PlayFeatureGateProps) {
  const { hasAccess, isLoading } = useMatchPlayAccess();
  const requiredTier = getRequiredTier(TierFeatureKey.MATCH_PLAY);

  if (isLoading) {
    return (
      <div className="flex min-h-[160px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <DashboardEmptyStateCard>
        <DashboardEmptyStateIcon icon={Trophy} />
        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">
          Match play
        </p>
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mx-auto mb-6 max-w-md text-gray-500 dark:text-gray-400">
          {description ??
            `Upgrade to ${requiredTier} or add a Club plan to run social play, round robins, and tournaments.`}
        </p>
        <Link to={SETTINGS_SUBSCRIPTION_HREF}>
          <Button size="lg">
            <Lock className="mr-2 h-4 w-4" aria-hidden />
            View subscription
          </Button>
        </Link>
      </DashboardEmptyStateCard>
    );
  }

  return <>{children}</>;
}
